import { createApi } from "@reduxjs/toolkit/query/react";
import authBaseQuery from "../../../../utils/authBaseQuery";
import jwtDecode from "jwt-decode";

const DirectPurchaseApi = createApi({
  reducerPath: "DirectPuchaseApi",
  baseQuery: authBaseQuery,
  endpoints: (builder) => ({
    getVendors: builder.query({
      query: ({ page, limit, search } = {}) => {
        const queryParams = [];
        if (page) queryParams.push(`page=${page}`);
        if (limit) queryParams.push(`limit=${limit}`);
        if (search) queryParams.push(`search=${encodeURIComponent(search)}`);

        const queryString =
          queryParams.length > 0 ? `?${queryParams.join("&")}` : "";
        return `v1/vendor_v2${queryString}`;
      },
      transformResponse: (response) => response?.data,
    }),

    getVendorsWithSearch: builder.query({
      query: (query) => {
        if (typeof query === "string" && query.trim() !== "") {
          return `v1/vendor_v2?search=${encodeURIComponent(
            query,
          )}&page=1&limit=10`;
        }
        return `v1/vendor_v2?page=1&limit=10`;
      },
      transformResponse: (response) => response?.data?.data,
    }),
    addService: builder.mutation({
      query: (data) => ({
        url: `v1/operation/add_service_in_campaign`,
        method: "POST",
        body: data,
      }),
    }),
    auditReport: builder.mutation({
      query: (data) => ({
        url: `v1/purchase/get_report_for_audit_status`,
        method: "POST",
        body: data,
      }),
    }),
    refetchPostPrice: builder.mutation({
      query: (data) => ({
        url: `v1/purchase/refetch_price_for_posts`,
        method: "POST",
        body: data,
      }),
    }),
    recordPurchase: builder.mutation({
      query: (data) => ({
        url: `v1/operation/record_purchase`,
        method: "POST",
        body: data,
      }),
    }),
    updatePurchasedStatusData: builder.mutation({
      query: ({
        amount,
        shortCode,
        updatedBy,
        pageName,
        campaignId,
        platform_id,
        phaseDate,
        platform_name,
      }) => ({
        url: `v1/purchase/update_purchased_status_data`,
        method: "PUT",
        body: {
          amount,
          shortCode,
          updatedBy,
          pageName,
          campaignId,
          platform_id,
          phaseDate,
          platform_name,
        },
      }),
    }),
    updateMultiplePurchasedStatusData: builder.mutation({
      query: ({ amount, shortCodes, updatedBy }) => ({
        url: `v1/purchase/update_purchased_status_multiple_data`,
        method: "PUT",
        body: { amount, shortCodes, updatedBy },
      }),
    }),
    updatePurchasedStatusVendor: builder.mutation({
      query: ({ shortCodes, vendor_id, userId }) => ({
        url: `v1/purchase/update_purchased_status_vendor`,
        method: "PUT",
        body: { shortCodes, vendor_id, userId },
      }),
    }),
    getLedger: builder.query({
      query: ({ id, query }) => `v1/get_vendor_ledger_details/${id}?${query}`,
      transformResponse: (response) => response?.data,
    }),
    getTotalData: builder.query({
      query: () => `v1/get_total_data`,
      transformResponse: (response) => response?.data,
    }),
    getVendorOutstanding: builder.query({
      query: (range) => `v1/get_vendor_outstanding/${range}`,
      transformResponse: (response) => response?.data,
    }),
    getCountOfUnregisteredPages: builder.query({
      query: () => `v1/get_count_of_unregistered_pages`,
      transformResponse: (response) => response?.data,
    }),
    getVendorPendingAuditedOutstanding: builder.query({
      query: (vendor_id) =>
        `v1/get_vendor_pending_audited_outstanding/${vendor_id}`,
      transformResponse: (response) => response?.data,
    }),
    getVendorAdvancedPayment: builder.query({
      query: ({ id, query }) => `purchase/advanced_payment/${id}?${query}`,
      transformResponse: (response) => response?.data,
    }),
    getVendorDetail: builder.query({
      query: (vendor_id) => `v1/vendor/${vendor_id}`,
      transformResponse: (response) => response?.data,
    }),
    getVendorAdvanceSummaryAndDetails: builder.query({
      query: (range) => `v1/get_vendor_advance_summary_and_details/${range}`,
      // ${range}?page=${page}&limit=${limit}
      transformResponse: (response) => response?.data,
    }),
    getVendorLedgerMonthWise: builder.query({
      query: ({ startDate, endDate }) => {
        const params = new URLSearchParams();

        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);

        return `v1/get_vendor_ledger_month_wise?${params.toString()}`;
      },
      transformResponse: (response) => response?.data,
    }),
    getAuditedAndPendingLinkStatsByVendors: builder.mutation({
      query: ({ page = 1, limit = 10, audit_status }) => ({
        url: `v1/purchase/get_audited_and_pending_link_stats_by_vendors`,
        method: "POST",
        body: { page, limit, audit_status },
      }),
      transformResponse: (response) => response?.data,
    }),
    getVendorWiseAdvancedPaymentDetails: builder.query({
      query: () => "purchase/vendor_wise_advanced_payment_details",
      transformResponse: (response) => response?.data,
    }),

    getPageWiseAdvancedPaymentDetails: builder.query({
      query: () => "purchase/page_wise_advanced_payment_details",
      transformResponse: (response) => response?.data,
    }),
    getLedgerAmountByVendor: builder.query({
      query: (id) => `v1/purchase/get_ledger_amt_by_vendor/${id}`,
      transformResponse: (response) => response?.data,
    }),

    getAdvancePaymentsByPageAndVendor: builder.query({
      query: ({ vendor_obj_id, page_name }) => {
        const params = new URLSearchParams();

        if (vendor_obj_id) params.append("vendor_obj_id", vendor_obj_id);
        if (page_name) params.append("page_name", page_name);

        return `purchase/advanced_payment?${params.toString()}`;
      },
      transformResponse: (response) => response?.data,
    }),
    getLinksOnCondition: builder.query({
      query: ({ audit_status, vendorId }) => ({
        url: "/v1/purchase/get_links_on_the_basis_of_condition",
        method: "POST",
        body: {
          matchCondition: {
            audit_status,
            vendorId,
          },
        },
      }),
    }),
    getPurchaseOverviewWithPagination: builder.query({
      queryFn: async (
        { startDate, endDate, page, limit, search } = {},
        _queryApi,
        _extraOptions,
        baseQuery,
      ) => {
        const storedToken = sessionStorage.getItem("token");
        if (!storedToken) {
          return { error: { status: 401, message: "No token found" } };
        }

        jwtDecode(storedToken);

        const queryParams = [];
        if (startDate)
          queryParams.push(`startDate=${encodeURIComponent(startDate)}`);
        if (endDate) queryParams.push(`endDate=${encodeURIComponent(endDate)}`);
        if (page) queryParams.push(`page=${page}`);
        if (limit) queryParams.push(`limit=${limit}`);
        if (search) queryParams.push(`search=${search}`);

        const queryString =
          queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

        const res = await baseQuery({
          url: `sales/purchase_overview${queryString}`,
          method: "GET",
        });

        if (res.error) {
          return { error: res.error };
        }

        return { data: res.data || res.data };
      },
      keepUnusedDataFor: 60 * 10,
      providesTags: ["purchaseOverview"],
      refetchOnMountOrArgChange: true, // 👈 HERE!
    }),
    getPurchaseUserData: builder.query({
      query: () => "get_users",
      transformResponse: (response) => response?.data?.paginatedUsers,
    }),

    advanceVendorManage: builder.mutation({
      query: ({ vendorObjId, is_advance_vendor }) => ({
        url: `v1/advance_vendor_manage/${vendorObjId}`,
        method: "PUT",
        body: {
          is_advance_vendor,
        },
      }),
    }),
    addUpfrontToAdvance: builder.mutation({
      query: ({ id, upfrontToAdvance }) => ({
        url: `purchase/upfront_to_advance_convert/${id}`,
        method: "PUT",
        body: {
          upfrontToAdvance,
        },
      }),
    }),

    updateCollabPostsPrice: builder.mutation({
      query: (payload) => ({
        url: "/v1/purchase/update_collab_posts_price",
        method: "PUT",
        body: payload,
      }),
    }),
    getAuditedAndPendingAdvancedLinkStatsByVendors: builder.query({
      query: ({ page, limit, audit_status } = {}) => {
        const queryParams = [];
        if (page) queryParams.push(`page=${page}`);
        if (limit) queryParams.push(`limit=${limit}`);
        if (audit_status)
          queryParams.push(`audit_status=${encodeURIComponent(audit_status)}`);

        const queryString =
          queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

        return `v1/get_audited_and_pending_advanced_link_stats_by_vendors${queryString}`;
      },
      transformResponse: (response) => response,
    }),

    getLinksForVendorWithActiveAdvance: builder.query({
      query: ({ vendorId, audit_status, page, limit } = {}) => {
        const queryParams = [];
        if (vendorId)
          queryParams.push(`vendorId=${encodeURIComponent(vendorId)}`);
        if (audit_status)
          queryParams.push(`audit_status=${encodeURIComponent(audit_status)}`);
        if (page) queryParams.push(`page=${page}`);
        if (limit) queryParams.push(`limit=${limit}`);

        const queryString =
          queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

        return `v1/get_links_for_vendor_with_active_advance${queryString}`;
      },
      transformResponse: (response) => response?.data,
    }),

    getVendorInfoWithAuditPurchasePendingStats: builder.query({
      query: ({ page = 1, limit = 100, vendorId } = {}) => {
        const queryParams = [];
        if (page) queryParams.push(`page=${encodeURIComponent(page)}`);
        if (limit) queryParams.push(`limit=${encodeURIComponent(limit)}`);
        if (vendorId) queryParams.push(`vendorId=${vendorId}`);

        const queryString =
          queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

        return `v1/vendor_info_with_audit_purchase_pending_stats${queryString}`;
      },
    }),

    getPurchaseOverviewByCampaignId: builder.query({
      query: ({ campaignId }) => {
        const queryParams = new URLSearchParams();
        if (campaignId) queryParams.append("campaignId", campaignId);

        return `sales/purchase_overview?${queryParams.toString()}`;
      },
    }),

    getAvgCampaignClosing: builder.query({
      query: ({ page = 1, limit = 100000 } = {}) => {
        return `get_avg_campaign_closing?page=${page}&limit=${limit}`;
      },
      transformResponse: (response) => response?.data,
    }),

    getCategoryWiseCampaignReport: builder.query({
      query: ({ campaignId }) => {
        return `get_category_wise_campaign_report?campaignId=${campaignId}`;
      },
      transformResponse: (response) => response?.data,
    }),
    getSalesBookingDetailsByCampaignId: builder.query({
      query: ({ campaignId }) => `report/sales_booking_details/${campaignId}`,
      transformResponse: (response) => response?.data,
    }),
    addCampaignPostForPlatform: builder.mutation({
      query: (body) => ({
        url: `/v1/operation/add_campaign_post_for_platform`,
        method: "POST",
        body,
      }),
    }),
    updateDefaultPageNameForLinks: builder.mutation({
      query: (payload) => ({
        url: `v1/purchase/update_default_page_name_for_links`,
        method: "POST",
        body: payload,
      }),
    }),
    getCampaignProfileById: builder.query({
      query: (id) => `/campaign_profile/${id}`,
    }),
    getAuthDetails: builder.query({
      query: (key) => `/count_user_auth/?${key ? "key_name=" + key : ""}`,
    }),
    // api/purchase/advanced_payment/66827bcf8e6fbfb72f5c8afe?startDate=2025-03-04&&endDate=2025-03-04

    // New Purchase Dashboard API'S
    getOutstandingAgingReport: builder.query({
      query: ({ query }) => `v1/get_outstanding_aging_report?${query}`,
      transformResponse: (response) => response?.data,
    }),
    getDayWiseOutstandingAgingReport: builder.query({
      query: ({ query }) => `v1/get_day_wise_outstanding_aging_report?${query}`,
      transformResponse: (response) => response?.data,
    }),
    getVendorFundRequirementPlan: builder.query({
      query: ({ query }) => `v1/get_vendor_fund_requirement_plan?${query}`,
      transformResponse: (response) => response?.data,
    }),
    getVendorInvoiceAging: builder.query({
      query: ({ query }) => `v1/get_vendor_invoice_aging?${query}`,
      transformResponse: (response) => response?.data,
    }),
    getVendorInvoiceDayWise: builder.query({
      query: ({ query }) => `v1/get_vendor_invoice_day_wise?${query}`,
      transformResponse: (response) => response?.data,
    }),
    getVendorPaymentAging: builder.query({
      query: ({ query }) => `v1/get_vendor_payment_aging?${query}`,
      transformResponse: (response) => response?.data,
    }),
    getVendorPaymentDayWise: builder.query({
      query: ({ query }) => `v1/get_vendor_payment_day_wise?${query}`,
      transformResponse: (response) => response?.data,
    }),
  }),
});

export const {
  useGetVendorsQuery,
  useGetVendorsWithSearchQuery,
  useAddServiceMutation,
  useAuditReportMutation,
  useRefetchPostPriceMutation,
  useRecordPurchaseMutation,
  useGetLedgerQuery,
  useUpdatePurchasedStatusDataMutation,
  useUpdateMultiplePurchasedStatusDataMutation,
  useUpdatePurchasedStatusVendorMutation,
  useGetTotalDataQuery,
  useGetVendorOutstandingQuery,
  useGetCountOfUnregisteredPagesQuery,
  useGetVendorPendingAuditedOutstandingQuery,
  useGetVendorAdvancedPaymentQuery,
  useGetVendorDetailQuery,
  useGetVendorAdvanceSummaryAndDetailsQuery,
  useGetVendorLedgerMonthWiseQuery,
  useGetAuditedAndPendingLinkStatsByVendorsMutation,
  useGetVendorWiseAdvancedPaymentDetailsQuery,
  useGetPageWiseAdvancedPaymentDetailsQuery,
  useGetLedgerAmountByVendorQuery,
  useLazyGetAdvancePaymentsByPageAndVendorQuery,
  useGetLinksOnConditionQuery,
  useGetPurchaseOverviewWithPaginationQuery,
  useGetPurchaseUserDataQuery,
  useAdvanceVendorManageMutation,
  useAddUpfrontToAdvanceMutation,
  useUpdateCollabPostsPriceMutation,
  useGetAuditedAndPendingAdvancedLinkStatsByVendorsQuery,
  useLazyGetLinksForVendorWithActiveAdvanceQuery,
  useGetVendorInfoWithAuditPurchasePendingStatsQuery,
  useGetPurchaseOverviewByCampaignIdQuery,
  useGetAvgCampaignClosingQuery,
  useGetCategoryWiseCampaignReportQuery,
  useGetSalesBookingDetailsByCampaignIdQuery,
  useAddCampaignPostForPlatformMutation,
  useUpdateDefaultPageNameForLinksMutation,
  useGetCampaignProfileByIdQuery,
  useGetAuthDetailsQuery,
  // New Purchase Dashboard API'S
  useGetOutstandingAgingReportQuery,
  useGetDayWiseOutstandingAgingReportQuery,
  useGetVendorFundRequirementPlanQuery,
  useGetVendorInvoiceAgingQuery,
  useGetVendorInvoiceDayWiseQuery,
  useGetVendorPaymentAgingQuery,
  useGetVendorPaymentDayWiseQuery,

} = DirectPurchaseApi;

export default DirectPurchaseApi;
